/**
 * Chat - in-game chat with dictionary lookup
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { socketActions } from '../../lib/socketActions';
import { useGameStore, type ChatMessage } from '../../stores/gameStore';
import { getPlayerColor } from '../../utils/playerColors';
import { REACTION_EMOJIS } from './EmojiReactions';

// Client-side rate limiting constants
const CHAT_RATE_LIMIT_MS = 500; // Min time between messages

export function Chat() {
  const chatMessages = useGameStore((s) => s.chatMessages);
  const playerId = useGameStore((s) => s.playerId);
  const [text, setText] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [lastSeenTimestamp, setLastSeenTimestamp] = useState(Date.now());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const rafIdRef = useRef<number | null>(null);
  const lastSendTimeRef = useRef<number>(0);

  // Auto-scroll to bottom when new messages arrive
  // Use requestAnimationFrame to ensure DOM is ready after render/animation
  // Cancel pending RAF to avoid race conditions with rapid messages
  useEffect(() => {
    let mounted = true;

    if (isExpanded && messagesEndRef.current) {
      // Cancel any pending scroll animation
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        // Check mounted flag to prevent scroll after unmount
        if (mounted && messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }

    // Cleanup on unmount
    return () => {
      mounted = false;
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [chatMessages, isExpanded]);

  const handleOpen = () => {
    setLastSeenTimestamp(Date.now());
    setIsExpanded(true);
  };

  const handleClose = () => {
    setLastSeenTimestamp(Date.now());
    setIsExpanded(false);
  };

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSendTimeRef.current < CHAT_RATE_LIMIT_MS) {
      useGameStore.getState().addToast({ type: 'info', message: 'Slow down!', duration: 1000 });
      return;
    }
    lastSendTimeRef.current = now;

    // Check if it's a dictionary lookup command
    if (trimmed.startsWith('/lookup ') || trimmed.startsWith('/d ')) {
      const word = trimmed.replace(/^\/(lookup|d)\s+/, '').toUpperCase();
      if (word) {
        socketActions.lookupWord(word);
      }
    } else {
      socketActions.sendChat(trimmed);
    }
    setText('');
  }, [text]);

  const unreadCount = isExpanded ? 0 : chatMessages.filter(
    (m) => m.timestamp > lastSeenTimestamp && m.playerId !== playerId && !m.isSystem
  ).length;

  // Esc inside the panel closes chat (and must not toggle pause)
  const handlePanelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
    }
  };

  return (
    <div className="chat-dock fixed z-40" data-no-claim-focus>
      {/* Chat toggle button (when collapsed) */}
      {!isExpanded && (
        <button
          onClick={handleOpen}
          className="touch-target relative flex items-center justify-center w-12 h-12 bg-white dark:bg-slate-800 rounded-full shadow-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          aria-label="Open chat"
        >
          <svg
            className="w-6 h-6 text-slate-600 dark:text-slate-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Expanded chat panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="chat-panel flex flex-col w-[min(320px,calc(100vw-2rem))] bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
            role="region"
            aria-label="Chat"
            onKeyDown={handlePanelKeyDown}
          >
            {/* Header - shrink-0 keeps the close button reachable on short screens */}
            <div className="shrink-0 flex items-center justify-between px-4 py-2 sm:py-3 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
              <h3 className="font-semibold text-slate-700 dark:text-slate-200">Chat</h3>
              <button
                onClick={handleClose}
                className="touch-target flex items-center justify-center p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded"
                aria-label="Close chat"
              >
                <svg
                  className="w-5 h-5 text-slate-500 dark:text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Messages */}
            <div className="h-64 min-h-[3rem] shrink overflow-y-auto overscroll-contain p-3 space-y-2 custom-scrollbar bg-white dark:bg-slate-800">
              {chatMessages.length === 0 ? (
                <div className="text-sm text-slate-400 dark:text-slate-500 text-center py-4">
                  No messages yet.
                  <br />
                  <span className="text-xs">
                    Type /lookup WORD to check dictionary
                  </span>
                </div>
              ) : (
                chatMessages.map((msg) => (
                  <ChatBubble
                    key={msg.id}
                    message={msg}
                    isOwnMessage={msg.playerId === playerId}
                  />
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick reactions */}
            <div className="shrink-0 flex items-center gap-1 px-3 py-1.5 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
              <span className="text-xs text-slate-400 mr-1">React:</span>
              {REACTION_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => socketActions.sendChat(emoji)}
                  aria-label={`React ${emoji}`}
                  className="w-8 h-8 flex items-center justify-center text-base hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                >
                  {emoji}
                </button>
              ))}
            </div>

            {/* Input */}
            <form
              onSubmit={handleSubmit}
              className="shrink-0 flex gap-2 p-2 sm:p-3 border-t border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800"
            >
              <input
                ref={inputRef}
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type a message..."
                enterKeyHint="send"
                aria-label="Chat message"
                className="flex-1 min-w-0 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
                maxLength={200}
              />
              <button
                type="submit"
                disabled={!text.trim()}
                aria-label="Send"
                className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface ChatBubbleProps {
  message: ChatMessage;
  isOwnMessage: boolean;
}

function ChatBubble({ message, isOwnMessage }: ChatBubbleProps) {
  if (message.isSystem) {
    // Special styling for dictionary lookup results
    if (message.isLookup) {
      return (
        <div
          className={`text-center text-xs py-2 px-3 rounded font-medium ${
            message.isValid
              ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700'
              : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-700'
          }`}
        >
          {message.text}
        </div>
      );
    }
    return (
      <div className="text-center text-xs text-slate-500 dark:text-slate-400 py-1 px-2 bg-slate-50 dark:bg-slate-700 rounded">
        {message.text}
      </div>
    );
  }

  // Get player color for the message
  const playerColor = message.colorIndex !== undefined
    ? getPlayerColor(message.colorIndex)
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}
    >
      {!isOwnMessage && (
        <span className={`text-xs mb-0.5 font-medium ${playerColor?.text || 'text-slate-500'}`}>
          {message.playerName}
        </span>
      )}
      <div
        className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
          isOwnMessage
            ? `${playerColor?.bg || 'bg-blue-500'} text-white rounded-br-sm`
            : 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-100 rounded-bl-sm'
        }`}
        style={!isOwnMessage && playerColor ? { borderLeft: `3px solid ${playerColor.hex}` } : undefined}
      >
        {message.text}
      </div>
      <span className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
        {formatTime(message.timestamp)}
      </span>
    </motion.div>
  );
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
